import { PaneNode, TabState } from '../../types/pane.types';
import { LayoutState } from '../../types/pane.types';
import { DatasetId } from '../../types/id.types';

/**
 * タブから対象となるデータセットIDを取得する
 * 
 * 優先順位:
 * 1. 見えている（アクティブな）data-tableタブ
 *    - 複数ある場合はアクティブコンテナのものを優先
 * 2. 見えていない（非アクティブな）data-tableタブ
 *    - アクティブコンテナのものを優先
 * 
 * @param layout - 現在のレイアウト状態
 * @param tabId - 対象タブのID（使用されていません - 将来の拡張用）
 * @returns データセットID（見つからない場合はnull）
 */
export function findActiveDatasetIdForTab(layout: LayoutState, _tabId: string): DatasetId | null {
  // すべての見えているdata-tableタブを収集
  const visibleDataTableTabs = collectVisibleDataTableTabs(layout.rootPane);
  
  if (visibleDataTableTabs.length > 0) {
    // アクティブコンテナのタブがあればそれを優先
    const activeContainerTab = visibleDataTableTabs.find(
      tab => tab.containerId === layout.activeContainerId
    );
    if (activeContainerTab) {
      return activeContainerTab.datasetId;
    }
    
    // なければ最初に見つかったものを使用
    return visibleDataTableTabs[0].datasetId;
  }
  
  // 見えているタブがない場合は、開いているタブから探す
  return findAnyDataTableTab(layout.rootPane, layout.activeContainerId);
}

/**
 * 見えているdata-tableタブを収集
 * 
 * @param node - 探索対象のペインノード
 * @returns 見えているdata-tableタブの配列
 */
function collectVisibleDataTableTabs(node: PaneNode): Array<{ datasetId: DatasetId; containerId: string }> {
  const visibleTabs: Array<{ datasetId: DatasetId; containerId: string }> = [];
  
  if (!node) return visibleTabs;
  
  if (node.type === 'container') {
    // このコンテナのアクティブなタブがdata-tableかつdatasetIdを持っている場合
    const activeTab = node.tabs?.find((t: TabState) => t.id === node.activeTabId);
    if (activeTab?.type === 'data-table' && activeTab.metadata?.datasetId) {
      visibleTabs.push({
        datasetId: activeTab.metadata.datasetId as DatasetId,
        containerId: node.id
      });
    }
  } else if (node.type === 'split') {
    // 分割ノードの場合は両方の子ノードを探索
    visibleTabs.push(...collectVisibleDataTableTabs(node.leftChild));
    visibleTabs.push(...collectVisibleDataTableTabs(node.rightChild));
  }
  
  return visibleTabs;
}

/**
 * 任意のdata-tableタブを探してデータセットIDを取得
 * アクティブコンテナのタブを優先
 * 
 * @param node - 探索対象のペインノード
 * @param activeContainerId - アクティブなコンテナのID
 * @returns データセットID（見つからない場合はnull）
 */
function findAnyDataTableTab(node: PaneNode, activeContainerId?: string): DatasetId | null {
  if (!node) return null;
  
  // まずアクティブコンテナから探す
  if (activeContainerId) {
    const activeContainerResult = findDataTableInContainer(node, activeContainerId);
    if (activeContainerResult) {
      return activeContainerResult;
    }
  }
  
  // アクティブコンテナになければ、任意のコンテナから探す
  return findAnyDataTableInTree(node);
}

/**
 * 特定のコンテナからdata-tableタブを探す
 * 
 * @param node - 探索対象のペインノード
 * @param containerId - 探索対象のコンテナID
 * @returns データセットID（見つからない場合はnull）
 */
function findDataTableInContainer(node: PaneNode, containerId: string): DatasetId | null {
  if (!node) return null;
  
  if (node.type === 'container' && node.id === containerId) {
    const dataTableTab = node.tabs?.find((t: TabState) => 
      t.type === 'data-table' && 
      t.metadata?.datasetId
    );
    if (dataTableTab) {
      return (dataTableTab.metadata?.datasetId as DatasetId) || null;
    }
  } else if (node.type === 'split') {
    const leftResult = findDataTableInContainer(node.leftChild, containerId);
    if (leftResult) return leftResult;
    return findDataTableInContainer(node.rightChild, containerId);
  }
  
  return null;
}

/**
 * ツリー全体から任意のdata-tableタブを探す
 * 
 * @param node - 探索対象のペインノード
 * @returns データセットID（見つからない場合はnull）
 */
function findAnyDataTableInTree(node: PaneNode): DatasetId | null {
  if (!node) return null;
  
  if (node.type === 'container') {
    const dataTableTab = node.tabs?.find((t: TabState) => 
      t.type === 'data-table' && 
      t.metadata?.datasetId
    );
    if (dataTableTab) {
      return (dataTableTab.metadata?.datasetId as DatasetId) || null;
    }
  } else if (node.type === 'split') {
    const leftResult = findAnyDataTableInTree(node.leftChild);
    if (leftResult) return leftResult;
    return findAnyDataTableInTree(node.rightChild);
  }
  
  return null;
}